import { useState } from "react";
import { X } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { C, KidAvatar, hm, kidColour, minLabel } from "../lib/kidLook";

// The app's pop-up sheets on the web: Give or take time, and Send a message. A sheet rises from the bottom on a
// phone and sits in the middle on a computer.

export function Sheet({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-[rgba(30,34,28,0.35)] md:items-center md:p-6"
         onClick={onClose} data-testid={testid}>
      <div className="max-h-[92vh] w-full overflow-y-auto rounded-t-[28px] bg-[#F5F4F0] px-5 pb-8 pt-3 md:max-w-md md:rounded-[28px] md:pt-5"
           onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto mb-3 h-[5px] w-[38px] rounded-full bg-[#CFCDC6] md:hidden" />
        <div className="mb-3 flex items-center justify-between">
          {title ? <h3 className="text-[20px] font-bold text-[#2C342A]">{title}</h3> : <span />}
          <button onClick={onClose} className="rounded-full p-1.5 text-[#707A6E] hover:bg-[#F0EFEB]" aria-label="Close">
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Chips({ items, selected, onPick, selectedColour = C.ink }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => {
        const on = it === selected;
        return (
          <button key={it} type="button" onClick={() => onPick(it)}
                  className="rounded-[14px] border px-3.5 py-2.5 text-[14.5px] font-semibold"
                  style={on ? { background: selectedColour, borderColor: selectedColour, color: "#fff" }
                            : { background: "#fff", borderColor: C.line, color: C.ink }}>
            {it}
          </button>
        );
      })}
    </div>
  );
}

export function Toggle({ on, onChange, testid }) {
  return (
    <button type="button" role="switch" aria-checked={on} onClick={() => onChange(!on)} data-testid={testid}
            className="relative h-[31px] w-[51px] flex-none rounded-full transition-colors"
            style={{ background: on ? C.moss : "#E0DED8" }}>
      <span className="absolute top-[2px] h-[27px] w-[27px] rounded-full bg-white shadow transition-all"
            style={{ left: on ? 22 : 2 }} />
    </button>
  );
}

const GIVE = [10, 30, 60];
const TAKE = [5, 10, 15, 30];
const TAKE_REASONS = ["Not listening", "Rudeness", "Screen overuse", "Chore missed", "Other"];

/** Give or take time, as in the app. Give = let them on for N minutes, even at bedtime. */
export function TimeSheet({ kid, initialMode = "give", onClose, onDone }) {
  const [mode, setMode] = useState(initialMode);
  const [minutes, setMinutes] = useState(initialMode === "give" ? 30 : 5);
  const [custom, setCustom] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const choices = mode === "give" ? GIVE : TAKE;
  const chosen = parseInt(custom, 10) || (custom ? 0 : minutes);
  const left = kid.policy?.remaining_today_seconds || 0;
  const tint = mode === "give" ? C.moss : C.coral;

  const switchMode = (m) => { setMode(m); setMinutes(m === "give" ? 30 : 5); setCustom(""); setError(""); };

  const apply = async () => {
    setBusy(true); setError("");
    try {
      if (mode === "give") {
        await api.post(`/kids/${kid.id}/override`, { minutes: chosen });
        onDone?.(`${kid.name} has ${minLabel(chosen)} extra.`);
      } else {
        await api.post("/reductions", { kid_ids: [kid.id], minutes: chosen, category: reason, reason: note });
        onDone?.(`Took ${minLabel(chosen)} off ${kid.name}.`);
      }
      onClose();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "That didn't work. Check the internet and try again.");
    } finally { setBusy(false); }
  };

  return (
    <Sheet onClose={onClose} testid="time-sheet">
      <div className="mb-4 flex rounded-[13px] bg-[#E8E6DF] p-[3px]">
        {[["give", "＋ Give time"], ["take", "－ Take time"]].map(([m, l]) => (
          <button key={m} type="button" onClick={() => switchMode(m)} data-testid={`time-sheet-${m}`}
                  className={`flex-1 rounded-[10px] py-2 text-[14.5px] font-semibold ${mode === m ? "bg-white text-[#2C342A] shadow-sm" : "text-[#707A6E]"}`}>
            {l}
          </button>
        ))}
      </div>
      <div className="text-[21px] font-bold text-[#2C342A]">
        {mode === "give" ? `Give ${kid.name} more time` : `Take time off ${kid.name}`}
      </div>
      <div className="mb-4 mt-0.5 text-[13.5px] text-[#707A6E]">
        {mode === "give"
          ? (left > 0 ? `${kid.name} has ${hm(left)} left today. This works even at bedtime.`
                      : `${kid.name} has used all of today's time. This works even at bedtime.`)
          : `Comes off what's left today. You can undo it from ${kid.name}'s page.`}
      </div>
      <Chips items={[...choices.map(minLabel), "Other"]}
             selected={custom ? "Other" : minLabel(minutes)}
             selectedColour={mode === "give" ? C.ink : C.coral}
             onPick={(p) => {
               if (p === "Other") setCustom(String(minutes));
               else { setCustom(""); setMinutes(choices.find((c) => minLabel(c) === p) || minutes); }
             }} />
      {custom !== "" && (
        <div className="mt-3 flex items-center gap-2">
          <input type="number" min="1" value={custom} onChange={(e) => setCustom(e.target.value)} autoFocus
                 className="w-28 rounded-[14px] border border-[#E4E2DC] bg-white px-3.5 py-3 outline-none focus:border-[#5C7A4F]" />
          <span className="text-[#707A6E]">minutes</span>
        </div>
      )}
      {mode === "take" && (
        <>
          <div className="mb-2 mt-5 text-[14.5px] font-semibold text-[#2C342A]">Reason ({kid.name} sees it)</div>
          <Chips items={TAKE_REASONS} selected={reason} selectedColour={C.coral}
                 onPick={(r) => setReason(reason === r ? "" : r)} />
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note (optional)"
                 className="mt-3 w-full rounded-[14px] border border-[#E4E2DC] bg-white px-3.5 py-3 outline-none focus:border-[#5C7A4F]" />
        </>
      )}
      {error && <div className="mt-3 text-sm text-[#C06E52]">{error}</div>}
      <button type="button" onClick={apply} disabled={busy || chosen <= 0} data-testid="time-sheet-apply"
              className="mt-5 w-full rounded-2xl py-4 text-[16.5px] font-semibold text-white disabled:opacity-50"
              style={{ background: tint }}>
        {busy ? "One moment…" : mode === "give" ? `Give ${kid.name} ${minLabel(chosen)}` : `Take ${minLabel(chosen)} off`}
      </button>
    </Sheet>
  );
}

const QUICK = ["🍽️ Dinner time!", "🌙 Time for bed", "🙋 Come and help, please", "👟 Time to get ready"];

/** Send a message to one or more kids, with an optional pause a few minutes later. */
export function MessageSheet({ kids, initialKidIds, onClose, onDone }) {
  const [ids, setIds] = useState(new Set(initialKidIds));
  const [text, setText] = useState("");
  const [pauseAfter, setPauseAfter] = useState(false);
  const [pauseMinutes, setPauseMinutes] = useState(5);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");

  const toggle = (id) => { const n = new Set(ids); n.has(id) ? n.delete(id) : n.add(id); setIds(n); };
  const chosenKids = kids.filter((k) => ids.has(k.id));
  const everyone = chosenKids.length > 1 && chosenKids.length === kids.length;
  const who = chosenKids.length === 1 ? chosenKids[0].name : `${chosenKids.length} kids`;

  const send = async () => {
    setBusy(true); setResult("");
    const names = [];
    try {
      for (const k of everyone ? [chosenKids[0]] : chosenKids) {
        const body = { text: text.trim(), all_kids: everyone, pause: pauseAfter };
        if (pauseAfter) body.pause_minutes = pauseMinutes;
        const { data } = await api.post(`/kids/${k.id}/message`, body);
        names.push(...(data.kids || []));
      }
      if (!names.length) { setResult("No device got it. Pair an iPhone, iPad or PC first."); return; }
      let line = `Sent to ${names.join(", ")}.`;
      if (pauseAfter) line += ` Pausing in ${pauseMinutes} min. Press Resume to cancel.`;
      onDone?.(line);
      onClose();
    } catch (e) {
      setResult(formatApiErrorDetail(e?.response?.data?.detail) || "That didn't send. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <Sheet title="Send a message" onClose={onClose} testid="message-sheet">
      <div className="mb-3.5 flex flex-wrap gap-2">
        {kids.map((k) => {
          const on = ids.has(k.id);
          return (
            <button key={k.id} type="button" onClick={() => toggle(k.id)}
                    className="flex items-center gap-1.5 rounded-full border py-1.5 pl-1.5 pr-3 text-sm font-semibold"
                    style={on ? { borderColor: C.moss, background: C.mossL, color: C.moss }
                              : { borderColor: C.line, background: "#fff", color: C.ink }}>
              <KidAvatar name={k.name} colour={kidColour(k.id, kids)} size={24} />
              {k.name}{on ? " ✓" : ""}
            </button>
          );
        })}
      </div>
      <Chips items={QUICK} selected={text} onPick={setText} />
      <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Or type your own"
             data-testid="message-text"
             className="mt-3 w-full rounded-[14px] border border-[#E4E2DC] bg-white px-3.5 py-3 outline-none focus:border-[#5C7A4F]" />
      <div className="card-soft mt-3 p-3.5">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <div className="text-[15px] text-[#2C342A]">Pause their devices after</div>
            <div className="text-xs text-[#707A6E]">Gives them time to save a game</div>
          </div>
          <Toggle on={pauseAfter} onChange={setPauseAfter} />
        </div>
        {pauseAfter && (
          <div className="mt-3">
            <Chips items={["1 min", "5 min", "10 min"]} selected={`${pauseMinutes} min`}
                   onPick={(p) => setPauseMinutes(parseInt(p, 10))} />
          </div>
        )}
      </div>
      {result && <div className="mt-3 text-sm text-[#707A6E]">{result}</div>}
      <button type="button" onClick={send} disabled={busy || !ids.size || !text.trim()} data-testid="message-send"
              className="mt-5 w-full rounded-2xl bg-[#5C7A4F] py-4 text-[16.5px] font-semibold text-white disabled:opacity-50">
        {busy ? "Sending…" : `Send to ${who}${pauseAfter ? ` · pause in ${pauseMinutes} min` : ""}`}
      </button>
    </Sheet>
  );
}
